import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { Toaster } from './components/ui/sonner';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import RoleGuard from './components/RoleGuard';
import AuthCallback from './components/AuthCallback';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import ClientsPage from './pages/Clients';
import ProductsPage from './pages/Products';
import TemplatesPage from './pages/Templates';
import DocumentsPage from './pages/Documents';
import DocumentWizard from './pages/DocumentWizard';
import DocumentDetail from './pages/DocumentDetail';
import LibrariesPage from './pages/Libraries';
import UsersPage from './pages/Users';
import AuditPage from './pages/Audit';
import SettingsPage from './pages/SettingsPage';
import UnitsPage from './pages/UnitsOfMeasure';
import PricesPage from './pages/Prices';
import MyTasksPage from './pages/MyTasks';
import QuestionBankPage from './pages/QuestionBank';
import OpportunitiesPage from './pages/Opportunities';
import OpportunityDetail from './pages/OpportunityDetail';
import ScreenManualsPage from './pages/ScreenManuals';
import VariablesPage from './pages/Variables';
import DocVariablesPage from './pages/DocVariables';
import VariableDictionaryPage from './pages/VariableDictionary';
import './App.css';

const ADMIN = ['Administrador'];
const CADASTROS = ['Administrador', 'Redator Sênior'];
const CADASTROS_PLUS = ['Administrador', 'Redator Sênior', 'Redator Padrão'];

function AppRouter() {
  const location = useLocation();
  
  // Legacy: handle old Emergent SSO callback with session_id in hash
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        {/* PRINCIPAL - todos */}
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="my-tasks" element={<MyTasksPage />} />
        <Route path="opportunities" element={<OpportunitiesPage />} />
        <Route path="opportunities/:opportunityId" element={<OpportunityDetail />} />
        <Route path="documents" element={<DocumentsPage />} />
        <Route path="documents/new" element={<DocumentWizard />} />
        <Route path="documents/:documentId" element={<DocumentDetail />} />

        {/* CADASTROS GERAIS - Admin + Sênior + Padrão (Clientes e Bibliotecas) */}
        <Route path="clients" element={<RoleGuard allowedRoles={CADASTROS_PLUS}><ClientsPage /></RoleGuard>} />
        <Route path="libraries" element={<RoleGuard allowedRoles={CADASTROS_PLUS}><LibrariesPage /></RoleGuard>} />

        {/* CADASTROS GERAIS - Admin + Sênior */}
        <Route path="products" element={<RoleGuard allowedRoles={CADASTROS}><ProductsPage /></RoleGuard>} />
        <Route path="templates" element={<RoleGuard allowedRoles={CADASTROS}><TemplatesPage /></RoleGuard>} />
        <Route path="question-bank" element={<RoleGuard allowedRoles={CADASTROS}><QuestionBankPage /></RoleGuard>} />
        <Route path="units" element={<RoleGuard allowedRoles={CADASTROS}><UnitsPage /></RoleGuard>} />
        <Route path="prices" element={<RoleGuard allowedRoles={CADASTROS}><PricesPage /></RoleGuard>} />
        <Route path="variables" element={<RoleGuard allowedRoles={CADASTROS}><VariablesPage /></RoleGuard>} />
        <Route path="doc-variables" element={<RoleGuard allowedRoles={CADASTROS}><DocVariablesPage /></RoleGuard>} />
        <Route path="variable-dictionary" element={<VariableDictionaryPage />} />

        {/* ADMINISTRAÇÃO - somente Admin */}
        <Route path="users" element={<RoleGuard allowedRoles={ADMIN}><UsersPage /></RoleGuard>} />
        <Route path="audit" element={<RoleGuard allowedRoles={ADMIN}><AuditPage /></RoleGuard>} />
        <Route path="settings" element={<RoleGuard allowedRoles={ADMIN}><SettingsPage /></RoleGuard>} />
        <Route path="screen-manuals" element={<RoleGuard allowedRoles={ADMIN}><ScreenManualsPage /></RoleGuard>} />

        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Route>
    </Routes>
  );
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </GoogleOAuthProvider>
  );
}

export default App;
